import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, CheckCircle2, Clock, ExternalLink, Facebook, Instagram, Loader2, ScrollText, Send, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

const CH = {
  instagram: { label: "Instagram", Icon: Instagram, color: "text-pink-500" },
  facebook: { label: "Facebook", Icon: Facebook, color: "text-blue-600" },
};

const TIMEZONES = ["Europe/Paris", "Europe/Zurich", "Europe/Brussels", "Europe/Luxembourg",
  "America/Montreal", "Africa/Casablanca", "Indian/Reunion"];

const fmtDT = (iso, tz) => {
  if (!iso) return "";
  const d = new Date(iso);
  const opts = { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" };
  if (tz) opts.timeZone = tz;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long" }) + " à "
    + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", ...(tz ? { timeZone: tz } : {}) });
};

function ChannelState({ ch, s, clientId }) {
  const { label, Icon, color } = CH[ch];
  if (!s || !s.status) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm py-1" data-testid={`publish-status-${ch}`}>
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="font-medium">{label}</span>
      {s.status === "published" && (
        <span className="inline-flex items-center gap-1.5 text-xs text-green-700">
          <CheckCircle2 className="w-3.5 h-3.5" /> Publiée le {fmtDT(s.published_at)}
          {s.permalink && (
            <a href={s.permalink} target="_blank" rel="noreferrer" data-testid={`publish-link-${ch}`}
              className="inline-flex items-center gap-0.5 text-blue-600 hover:underline font-medium">
              Voir la publication <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </span>
      )}
      {s.status === "publishing" && (
        <span className="inline-flex items-center gap-1.5 text-xs text-blue-600">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Publication en cours…
        </span>
      )}
      {s.status === "scheduled" && (
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500" data-testid={`publish-scheduled-${ch}`}>
          <Clock className="w-3.5 h-3.5" /> Programmée
        </span>
      )}
      {s.status === "failed" && (
        <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-red-600">
          <AlertTriangle className="w-3.5 h-3.5" /> {label} n'a pas pu être publié. {s.error_message}
          {s.error_code === "token_expired" && (
            <Link to={`/clients/${clientId}?tab=apercu`} className="font-semibold underline" data-testid={`publish-reconnect-${ch}`}>
              Reconnecter Meta
            </Link>
          )}
        </span>
      )}
    </div>
  );
}

export function PublishPanel({ pub, onChanged }) {
  const [pc, setPc] = useState(null);
  const [channels, setChannels] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logs, setLogs] = useState(null);
  const [showSched, setShowSched] = useState(false);
  const [confirmUnsched, setConfirmUnsched] = useState(false);
  const [schedDate, setSchedDate] = useState(pub.date || "");
  const [schedTime, setSchedTime] = useState(pub.time || "18:00");
  const [schedTz, setSchedTz] = useState(pub.timezone || "Europe/Paris");

  useEffect(() => {
    api.get(`/publications/${pub.id}/publish/precheck`).then((r) => {
      setPc(r.data);
      const sel = {};
      (r.data.channels || []).forEach((c) => (sel[c] = true));
      setChannels(sel);
    }).catch(() => setPc(null));
  }, [pub.id, pub.updated_at]);

  const publishing = pub.status === "publication_en_cours";
  useEffect(() => {
    if (!publishing) return undefined;
    const t = setInterval(() => onChanged(), 3000);
    return () => clearInterval(t);
  }, [publishing, onChanged]);

  const selected = Object.keys(channels).filter((c) => channels[c]);
  const cs = pub.channels_status || {};
  const conn = pc?.connection || {};
  const hasStates = Object.keys(cs).length > 0;
  const scheduled = pub.status === "programme" && pub.scheduled_at;
  const publishable = ["valide", "programme", "erreur_publication", "publie"].includes(pub.status);
  const canAct = pc?.ok && selected.length > 0 && publishable && !publishing && !busy;
  const failedChecks = (pc?.checks || []).filter((c) => !c.ok && c.blocking !== false);
  const warnChecks = (pc?.checks || []).filter((c) => !c.ok && c.blocking === false);

  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/publications/${pub.id}/publish`,
        { channels: selected, origin: window.location.origin });
      toast.success("Publication envoyée à Meta — exécution en cours");
      setConfirm(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de publication");
    } finally { setBusy(false); }
  };

  const schedule = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/publications/${pub.id}/schedule`, {
        channels: selected, date: schedDate, time: schedTime,
        timezone: schedTz, origin: window.location.origin,
      });
      toast.success(`Publication programmée pour le ${fmtDT(r.data.scheduled_at, r.data.timezone)}`);
      setShowSched(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de programmation");
    } finally { setBusy(false); }
  };

  const unschedule = async () => {
    setBusy(true);
    try {
      await api.post(`/publications/${pub.id}/unschedule`);
      toast.success("Programmation annulée — la publication repasse en « Validée »");
      setConfirmUnsched(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const toggleLogs = async () => {
    if (logs === null) {
      try {
        const r = await api.get(`/publications/${pub.id}/publish/logs`);
        setLogs(r.data);
      } catch { setLogs([]); }
    } else setLogs(null);
  };

  const accountName = (ch) =>
    ch === "instagram"
      ? (conn.instagram_username ? `@${conn.instagram_username}` : "aucun compte lié")
      : (conn.facebook_page_name || "aucune Page liée");

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm" data-testid="publish-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-heading font-bold text-sm flex items-center gap-2">
          <Send className="w-4 h-4" /> Publication automatique — Meta
        </h3>
        <button onClick={toggleLogs} className="text-xs text-neutral-400 hover:text-neutral-700 inline-flex items-center gap-1" data-testid="publish-logs-toggle">
          <ScrollText className="w-3.5 h-3.5" /> {logs === null ? "Logs techniques" : "Masquer les logs"}
        </button>
      </div>

      {/* Comptes connectés */}
      <div className="space-y-1 mb-3">
        {Object.entries(CH).map(([ch, { label, Icon, color }]) => {
          const ok = conn.connected && (ch === "instagram" ? !!conn.instagram_username : !!conn.facebook_page_name);
          return (
            <div key={ch} className="flex items-center gap-2 text-sm" data-testid={`publish-account-${ch}`}>
              <Icon className={`w-4 h-4 ${color}`} />
              <span className="font-medium">{label}</span>
              {conn.connected && ok ? (
                <span className="text-xs text-green-700 inline-flex items-center gap-1">
                  {accountName(ch)} <CheckCircle2 className="w-3.5 h-3.5" /> Connecté
                </span>
              ) : conn.connection_status === "token_expired" ? (
                <span className="text-xs text-amber-600 inline-flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> Connexion expirée —{" "}
                  <Link to={`/clients/${pub.client_id}?tab=apercu`} className="underline font-medium">reconnecter le compte</Link>
                </span>
              ) : (
                <span className="text-xs text-neutral-400">
                  {conn.connected ? `non disponible (${accountName(ch)})` : "Compte Meta non connecté"}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* État de programmation */}
      {scheduled && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2 text-sm" data-testid="publish-scheduled-info">
          <Clock className="w-4 h-4 text-green-600" />
          <span className="font-medium text-green-800">
            Programmée pour le {fmtDT(pub.scheduled_at, pub.timezone)} ({pub.timezone || "Europe/Paris"})
          </span>
          <span className="text-xs text-green-700">— publication automatique par le serveur, même sans utilisateur connecté.</span>
        </div>
      )}

      {publishable && conn.connected && (
        <div className="border-t border-neutral-100 pt-3 space-y-3">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider">Réseaux</span>
            {Object.entries(CH).map(([ch, { label }]) => {
              const available = ch === "instagram" ? !!conn.instagram_username : !!conn.facebook_page_name;
              const done = cs[ch]?.status === "published";
              return (
                <label key={ch} className={`flex items-center gap-2 text-sm ${available && !done ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                  <input type="checkbox" disabled={!available || done}
                    checked={!!channels[ch]}
                    onChange={(e) => setChannels((c) => ({ ...c, [ch]: e.target.checked }))}
                    data-testid={`publish-channel-${ch}`} />
                  {label}{done ? " (déjà publié)" : ""}
                </label>
              );
            })}
          </div>

          {failedChecks.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1" data-testid="publish-precheck">
              {failedChecks.map((c) => (
                <p key={c.label} className="text-xs text-amber-800 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span><b>{c.label}</b>{c.detail ? ` — ${c.detail}` : ""}</span>
                </p>
              ))}
            </div>
          )}
          {warnChecks.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1" data-testid="publish-precheck-warnings">
              {warnChecks.map((c) => (
                <p key={c.label} className="text-xs text-blue-800 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span><b>{c.label}</b>{c.detail ? ` — ${c.detail}` : ""} (non bloquant pour publier)</span>
                </p>
              ))}
            </div>
          )}

          {/* Formulaire de programmation */}
          {showSched ? (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 space-y-3" data-testid="schedule-box">
              <p className="text-sm font-semibold flex items-center gap-1.5">
                <CalendarClock className="w-4 h-4" /> Programmer la publication
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input type="date" className="mt-1" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="schedule-date" />
                </div>
                <div>
                  <Label className="text-xs">Heure</Label>
                  <Input type="time" className="mt-1" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} data-testid="schedule-time" />
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <Label className="text-xs">Fuseau</Label>
                  <Select value={schedTz} onValueChange={setSchedTz}>
                    <SelectTrigger className="mt-1" data-testid="schedule-timezone"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="text-xs text-neutral-500 space-y-0.5" data-testid="schedule-accounts">
                {selected.map((c) => (
                  <p key={c} className="flex items-center gap-1.5">
                    {c === "instagram" ? <Instagram className="w-3.5 h-3.5 text-pink-500" /> : <Facebook className="w-3.5 h-3.5 text-blue-600" />}
                    {CH[c].label} — {accountName(c)}
                  </p>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setShowSched(false)} data-testid="schedule-cancel-btn">Annuler</Button>
                <Button size="sm" onClick={schedule} disabled={!canAct || !schedDate || !schedTime}
                  className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="schedule-confirm-btn">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />}
                  Programmer
                </Button>
              </div>
            </div>
          ) : confirm ? (
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 space-y-2" data-testid="publish-confirm-box">
              <p className="text-sm">Cette publication sera immédiatement envoyée sur :</p>
              <ul className="text-sm space-y-0.5">
                {selected.map((c) => <li key={c}>• {CH[c].label} — {accountName(c)}</li>)}
              </ul>
              <p className="text-xs text-neutral-500">Continuer ?</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setConfirm(false)} data-testid="publish-cancel-btn">Annuler</Button>
                <Button size="sm" onClick={publish} disabled={busy} className="bg-green-600 hover:bg-green-700 text-white gap-1.5" data-testid="publish-confirm-btn">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Publier
                </Button>
              </div>
            </div>
          ) : confirmUnsched ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2" data-testid="unschedule-confirm-box">
              <p className="text-sm text-red-800">Annuler la programmation ? La publication repassera en « Validée » (rien ne sera publié automatiquement).</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setConfirmUnsched(false)} data-testid="unschedule-cancel-btn">Conserver</Button>
                <Button size="sm" onClick={unschedule} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white gap-1.5" data-testid="unschedule-confirm-btn">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Annuler la programmation
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2.5">
              <Button size="sm" onClick={() => setConfirm(true)} disabled={!canAct}
                className="bg-green-600 hover:bg-green-700 text-white gap-1.5" data-testid="publish-now-btn">
                {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {publishing ? "Publication en cours…" : "Publier maintenant"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowSched(true)} disabled={!canAct && !scheduled}
                className="gap-1.5" data-testid="schedule-btn">
                <CalendarClock className="w-3.5 h-3.5" /> {scheduled ? "Modifier la programmation" : "Programmer"}
              </Button>
              {scheduled && (
                <Button size="sm" variant="outline" onClick={() => setConfirmUnsched(true)}
                  className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50" data-testid="unschedule-btn">
                  <XCircle className="w-3.5 h-3.5" /> Annuler la programmation
                </Button>
              )}
              {!canAct && !publishing && pc && !pc.ok && (
                <span className="text-xs text-neutral-400">Impossible de publier — voir les alertes ci-dessus.</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* États par réseau */}
      {hasStates && (
        <div className="border-t border-neutral-100 pt-3 mt-3 space-y-0.5" data-testid="publish-channels-status">
          {Object.entries(cs).map(([ch, s]) => (
            <ChannelState key={ch} ch={ch} s={s} clientId={pub.client_id} />
          ))}
        </div>
      )}

      {/* Logs techniques (admin) */}
      {logs !== null && (
        <div className="border-t border-neutral-100 pt-3 mt-3" data-testid="publish-logs">
          {logs.length === 0 ? (
            <p className="text-xs text-neutral-400">Aucun log de publication.</p>
          ) : (
            <div className="space-y-1 max-h-56 overflow-y-auto">
              {logs.map((l) => (
                <p key={l.id} className="text-xs text-neutral-500 font-mono">
                  <span className="text-neutral-400">{fmtDT(l.created_at)}</span> — <b>{l.step}</b>
                  {l.detail ? <span className="text-neutral-400"> · {l.detail}</span> : null}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
